import {
  Alert,
  Badge,
  Checkbox,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  ApiError,
  api,
  type ProjectAccessUser,
  type ProjectSummary,
} from "@/api/client";

/**
 * One project at a time rather than a full user-by-project matrix: with
 * dozens of projects and users the matrix stops fitting a screen, and an
 * admin granting access is almost always thinking "who needs to see this
 * project," not "what does everyone have."
 */
export function AdminProjectAccessPage() {
  const [slug, setSlug] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const projects = useQuery<ProjectSummary[]>({
    queryKey: ["projects"],
    queryFn: () => api.get<ProjectSummary[]>("/api/projects"),
  });

  const accessKey = ["admin", "project-access", slug];
  const access = useQuery<ProjectAccessUser[]>({
    queryKey: accessKey,
    queryFn: () => api.get<ProjectAccessUser[]>(`/api/admin/projects/${slug}/access`),
    enabled: !!slug,
  });

  const toggle = useMutation({
    mutationFn: ({ userId, grant }: { userId: number; grant: boolean }) =>
      grant
        ? api.put<{ detail: string }>(`/api/admin/projects/${slug}/grants/${userId}`)
        : api.delete<{ detail: string }>(`/api/admin/projects/${slug}/grants/${userId}`),
    onMutate: async ({ userId, grant }) => {
      // Flip the checkbox immediately; roll back on failure rather than
      // waiting a round trip to reflect what the admin just clicked.
      await queryClient.cancelQueries({ queryKey: accessKey });
      const previous = queryClient.getQueryData<ProjectAccessUser[]>(accessKey);
      queryClient.setQueryData<ProjectAccessUser[]>(accessKey, (rows) =>
        rows?.map((r) => (r.id === userId ? { ...r, has_access: grant } : r)),
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(accessKey, context.previous);
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not update access.",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: accessKey });
    },
  });

  return (
    <Stack gap="lg">
      <div>
        <Title order={2} mb="xs">
          Project access
        </Title>
        <Text c="dimmed">Choose a project to see who can view it.</Text>
      </div>

      <Select
        label="Project"
        placeholder={projects.data?.length ? "Select a project" : "No projects available"}
        data={(projects.data ?? []).map((p) => ({ value: p.slug, label: p.title }))}
        value={slug}
        onChange={setSlug}
        searchable
        maw={400}
      />

      {slug && access.isLoading && <Loader />}
      {slug && access.error && (
        <Alert color="red" title="Could not load access">
          {access.error instanceof Error ? access.error.message : "Unexpected error."}
        </Alert>
      )}

      {slug && access.data && (
        <Table.ScrollContainer minWidth={520}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Email</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Access</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {access.data.map((user) => (
                <Table.Tr key={user.id}>
                  <Table.Td>{user.email}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c={user.display_name ? undefined : "dimmed"}>
                      {user.display_name ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={user.system_role === "admin" ? "indigo" : "gray"}
                    >
                      {user.system_role}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip
                      label="Admins already have access to every project"
                      disabled={user.system_role !== "admin"}
                    >
                      <Checkbox
                        checked={user.has_access}
                        disabled={user.system_role === "admin" || toggle.isPending}
                        aria-label={`Grant ${user.email} access to ${slug}`}
                        onChange={(event) =>
                          toggle.mutate({
                            userId: user.id,
                            grant: event.currentTarget.checked,
                          })
                        }
                      />
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
